import React, {useEffect, useRef, useState} from "react";
import SendsAdminNavigation from "../../../Components/Sends/SendsAdminNavigation";
import FullWidthSidebar from "../../../Framework/Components/PageLayouts/FullWidthSidebar";
import BasicPageHeader from "../../../Framework/Components/LayoutScaffold/BasicPageHeader";
import {PaginatedResponseType} from "../../../Types/Response/PaginatedResponseType";
import SendsUserItem from "../../../Components/Sends/SendsUserItem";
import Button from "../../../Framework/Components/Buttons/Button";
import {
    disableUser, sendPasswordResetEmail,
    useInfiniteUsers,
    UserDataType,
    UsersUpdateType,
    useUpdateUsers
} from "../../../Api/UsersEndpoints";
import CreateUserModal from "../../../Components/Sends/Modals/CreateUserModal";
import {ColourMod, FlexAlign, FlexGap, FlexJustify} from "../../../Framework/Data/Enums/LayoutEnums";
import ConfirmDeleteUserModal from "../../../Components/Sends/Modals/ConfirmDeleteUserModal";
import StackVertical from "../../../Framework/Components/Wrappers/StackVertical";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBomb, faUser, faKey, faUsers} from "@fortawesome/pro-solid-svg-icons";
import InfiniteLoadTrigger from "../../../Framework/Components/InfiniteLoadTrigger";
import {GetItemCounts} from "../../../Framework/Constants/DataHelpers";
import {useLayout} from "../../../Framework/Providers/LayoutProvider";
import {useToasts} from "../../../Framework/Providers/ToastsProvider";

const SendsUsers = () => {
    const { addToast } = useToasts();
    const { isMedium, isLarge } = useLayout();

    const {
        status,
        data,
        error,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage
    } : {
        status: any,
        data: PaginatedResponseType | any,
        error: any,
        isFetching: any,
        isFetchingNextPage: any,
        fetchNextPage: any,
        hasNextPage?: any } = useInfiniteUsers();

    const [ createUserOpen , setCreateUserOpen ] = useState<boolean>(false);
    const [ activeUser, setActiveUser ] = useState<any>({ isOpen: false, user: null});
    const [ itemCounts, setItemCounts ] = useState<any>({ loaded: 0, total: 0 } );

    const updateUsers = useUpdateUsers();

    const sendsUsersRef = useRef<any>();

    const SendPasswordReset = (userId: number) => {
        sendPasswordResetEmail(userId).then(r => {
            if(r) {
                addToast('Password Reset Sent!', <FontAwesomeIcon icon={faKey} />, ColourMod.Success);
            } else {
                addToast('Password Reset Failed!', <FontAwesomeIcon icon={faKey} />, ColourMod.Alert);
            }
        });
    }

    const ShowDeleteConfirmation = (user: UserDataType) => {
        setActiveUser({ user: user, isOpen: true })
    }

    const HandleDisable = async (userId: number, disabled: boolean) => {
        disableUser(userId, disabled).then((r) => {
            updateUsers.mutate({
                updateType: UsersUpdateType.DISABLE,
                newData: userId,
                forwardedFunction: null
            })
        });
    }

    const ItemPlaceholder = () => {
        return (
            <div className={'content-loader content-fader'} style={{ display: 'grid', gap: 'var(--sm-unit)', margin: '0 var(--md-unit)', gridTemplateColumns: isMedium ? 'repeat(3, 1fr) 5em' : '1fr 5em'}}>
                {isMedium ? (
                    <>
                        <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
                        <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
                        <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
                        <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
                    </>
                ) : (
                    <>
                        <div style={{ width: 'auto', height: '2em' }} />
                        <div style={{ width: 'auto', height: '2em' }} />
                    </>
                )}
            </div>
        )
    }

    const PageActions = () => {
        return (
            <Button
                colour={ColourMod.Primary}
                onClick={() => setCreateUserOpen(true)}
                squared={true}
                title={'Add User'}
                icon={<FontAwesomeIcon icon={faUser} />}
            />
        )
    }

    useEffect(() => {
        if(data) {
            setItemCounts(GetItemCounts(data));
        }
    }, [data]);

    useEffect(() => {
        document.title = 'Users Admin'
    }, []);

    return (
        <FullWidthSidebar
            ref={sendsUsersRef}
            navigationComponent={
                <>
                    <SendsAdminNavigation
                        title={'Users'}
                        addonContent={
                            <div className={'section-navigation-header-collapsed-addon'}>
                                {!isLarge && (
                                    <PageActions />
                                )}
                            </div>
                        }
                    />
                </>
            }
            extraContentClass={`flex-column `}
        >
            {isLarge && (
                <BasicPageHeader title={'Users'}>
                    <PageActions />
                </BasicPageHeader>
            )}

            {isMedium && (
                <div className={'item-row item-row-header pl-md pr-md'} style={{ gridTemplateColumns:
                        'repeat(3, 1fr) 5em' }}>
                    <div className={'sm-sub-heading'}>Name</div>
                    <div className={'sm-sub-heading'}>Email Address</div>
                   {/* <div className={'sm-sub-heading'}>Roles</div>*/}
                    <div className={'sm-sub-heading'}>Actions</div>
                    <div className={'sm-sub-heading'}>{/*Active*/}</div>
                </div>
            )}

            {status === "loading" ? (
                <div className={'content-fader'}>
                    <ItemPlaceholder />
                    <ItemPlaceholder />
                    <ItemPlaceholder />
                    <ItemPlaceholder />
                    <ItemPlaceholder />
                    <ItemPlaceholder />
                    <ItemPlaceholder />
                </div>
            ) : status === "error" ? (
                <StackVertical gap={FlexGap.ExtraSmall} align={FlexAlign.Center} justify={FlexJustify.Center} extraClass={'m-xxl p-xxl text-center'}>
                    <FontAwesomeIcon icon={faBomb} size={"3x"} />
                    <h6 className={'sub-heading mt-sm mb-xs'}>Users Error</h6>
                    <p>{error.message}</p>
                </StackVertical>
            ) : (
                <>
                    {data && data.pages.map((group: any, groupIndex: number) =>
                        group.data.map((entry: UserDataType, index: number) =>
                            <SendsUserItem
                                key={entry.userId}
                                {...entry}
                                disableCallback={(checked: boolean) => HandleDisable(entry.userId, checked)}
                                passwordResetCallback={() => SendPasswordReset(entry.userId)}
                                deleteCallback={() => ShowDeleteConfirmation(entry)}
                            />
                        )
                    )}

                    {itemCounts.total === 0 && (
                        <StackVertical gap={FlexGap.ExtraSmall} align={FlexAlign.Center} justify={FlexJustify.Center} extraClass={'m-xxl p-xxl text-center'}>
                            <FontAwesomeIcon icon={faUsers} size={"3x"} />
                            <h6 className={'sub-heading mt-sm mb-xs'}>No Users</h6>
                            <p>You haven't added any users yet</p>
                            <Button title={'Get Started'} colour={ColourMod.Primary} onClick={() => setCreateUserOpen(true)} />
                        </StackVertical>
                    )}

                    {itemCounts.total > 0 && (
                        <InfiniteLoadTrigger
                            ref={sendsUsersRef.current}
                            loaded={itemCounts.loaded}
                            totalCount={itemCounts.total}
                            hasNextPage={hasNextPage}
                            fetchNextPage={fetchNextPage}
                            isFetchingNextPage={isFetchingNextPage}
                        />
                    )}
                </>
            )}

            {createUserOpen && (
                <CreateUserModal visible={createUserOpen} closeCallback={() => setCreateUserOpen(false) } />
            )}

            {activeUser.isOpen && (
                <ConfirmDeleteUserModal
                    user={activeUser.user}
                    isOpen={activeUser.isOpen}
                    closeCallback={() => setActiveUser({...activeUser, isOpen: false})}
                />
            )}

        </FullWidthSidebar>
    )
}

export default SendsUsers
